import React, { useState } from "react";
import styled from "styled-components";
import {
  FaCalendar,
  FaMapMarkerAlt,
  FaClock,
  FaTicketAlt,
  FaEnvelope,
} from "react-icons/fa";
import {
  colors,
  fonts,
  spacing,
  borderRadius,
  shadows,
  transitions,
} from "../styles/branding";
import { useFormSubmit } from "../hooks/useFormSubmit";

const EventsContainer = styled.div`
  padding-top: 80px;
`;

const HeroSection = styled.section`
  background: ${colors.gradient.primary};
  color: white;
  padding: 4rem 2rem;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  font-family: ${fonts.heading};
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
  font-family: ${fonts.body};
`;

const EventsSection = styled.section`
  padding: 5rem 2rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${colors.inkBlack};
  text-align: center;
  margin-bottom: 3rem;
  font-family: ${fonts.heading};
`;

const EventsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 3rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 0 1rem;
  }
`;

const EventCard = styled.div`
  background: white;
  border-radius: ${borderRadius.md};
  overflow: hidden;
  box-shadow: ${shadows.lg};
`;

const EventImage = styled.div`
  height: 250px;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
`;

const EventContent = styled.div`
  padding: 2rem;
`;

const EventTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: ${colors.inkBlack};
  font-family: ${fonts.heading};
  font-weight: 500;
`;

const EventDetails = styled.div`
  margin-bottom: 1.5rem;

  .detail {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    color: ${colors.coolTaupe};
    font-size: 0.9rem;
    font-family: ${fonts.body};
  }
`;

const EventDescription = styled.p`
  color: ${colors.coolTaupe};
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-family: ${fonts.body};
`;

const EventButton = styled.button`
  background: ${colors.dustyRose};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: ${borderRadius.lg};
  font-weight: bold;
  font-family: ${fonts.body};
  cursor: pointer;
  transition: ${transitions.normal};

  &:hover {
    background: ${colors.coolTaupe};
    transform: translateY(-2px);
  }
`;

const BookingSection = styled.section`
  padding: 5rem 2rem;
  background: ${colors.paleMauve};
`;

const BookingContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const BookingTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  color: ${colors.inkBlack};
  font-family: ${fonts.heading};
  font-weight: 600;
`;

const BookingForm = styled.form`
  background: white;
  padding: 3rem;
  border-radius: ${borderRadius.md};
  box-shadow: ${shadows.lg};

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 2rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: bold;
    color: ${colors.inkBlack};
    font-family: ${fonts.body};
  }

  input,
  textarea,
  select {
    width: 100%;
    padding: 1rem;
    border: 2px solid #eee;
    border-radius: 8px;
    font-size: 1rem;
    font-family: ${fonts.body};
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: ${colors.dustyRose};
    }
  }

  textarea {
    resize: vertical;
    min-height: 120px;
  }

  @media (max-width: 768px) {
    input,
    textarea,
    select {
      padding: 1.2rem;
      font-size: 1.1rem;
    }
  }
`;

const SubmitButton = styled.button`
  background: ${colors.dustyRose};
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: ${borderRadius.lg};
  font-weight: bold;
  font-size: 1rem;
  font-family: ${fonts.body};
  cursor: pointer;
  transition: ${transitions.normal};
  width: 100%;

  &:hover {
    background: ${colors.coolTaupe};
    transform: translateY(-2px);
  }
`;

const ThankYouMessage = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  background: ${colors.paleMauve};
  border-radius: ${borderRadius.lg};
  margin-top: 2rem;
`;

const ThankYouTitle = styled.h2`
  font-size: 2rem;
  color: ${colors.inkBlack};
  margin-bottom: 1.5rem;
  font-family: ${fonts.heading};
  font-weight: 600;
`;

const ThankYouText = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${colors.coolTaupe};
  margin-bottom: 1.5rem;
  font-family: ${fonts.body};
  font-style: italic;
`;

const BackButton = styled.button`
  background: ${colors.dustyRose};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: ${borderRadius.md};
  font-weight: bold;
  font-family: ${fonts.body};
  cursor: pointer;
  transition: ${transitions.normal};

  &:hover {
    background: ${colors.coolTaupe};
    transform: translateY(-2px);
  }
`;

const TestimonialsSection = styled.section`
  padding: 5rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  background: white;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 3rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 0 1rem;
  }
`;

const TestimonialCard = styled.div`
  background: white;
  border-radius: ${borderRadius.md};
  padding: 2.5rem;
  box-shadow: ${shadows.md};
  text-align: center;
`;

const TestimonialText = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${colors.coolTaupe};
  margin-bottom: 1.5rem;
  font-family: ${fonts.body};
  font-style: italic;
`;

const TestimonialAuthor = styled.p`
  font-size: 0.9rem;
  color: ${colors.coolTaupe};
  font-family: ${fonts.body};
  font-style: italic;
`;

const Events = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    eventType: "",
    date: "",
    location: "",
    audience: "",
    message: "",
  });
  const [showThankYou, setShowThankYou] = useState(false);

  const eventTypes = [
    {
      id: 1,
      title: "Weddings & Celebrations",
      image: "/images/events/wedding.jpg",
      description:
        "Create unforgettable moments at weddings, anniversaries, and romantic celebrations. Guests choose their subjects and receive personalized poems that become cherished keepsakes.",
      examples: [
        "Wedding receptions",
        "Engagement parties",
        "Anniversary celebrations",
        "Valentine's Day events",
      ],
    },
    {
      id: 2,
      title: "Corporate Events",
      image: "/images/events/corporate.jpg",
      description:
        "Add a unique, creative touch to corporate gatherings, team building events, and company celebrations. Poems can be branded with your company logo.",
      examples: [
        "Holiday parties",
        "Team building events",
        "Product launches",
        "Company anniversaries",
      ],
    },
    {
      id: 3,
      title: "Life Milestones",
      image: "/images/events/milestones.jpg",
      description:
        "Make birthdays, graduations, and life transitions extra special with custom poetry that captures the essence of the moment.",
      examples: [
        "Birthday parties",
        "Graduation celebrations",
        "Baby showers",
        "Retirement parties",
      ],
    },
    {
      id: 4,
      title: "Cultural & Community Events",
      image: "/images/events/cultural.jpg",
      description:
        "Enhance cultural festivals, art openings, and community gatherings with interactive poetry that brings people together.",
      examples: [
        "Art gallery openings",
        "Cultural festivals",
        "Community fundraisers",
        "Literary events",
      ],
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const { submitForm, isSubmitting, submitStatus, resetStatus } =
    useFormSubmit();

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Format the message with all form fields except name and email, two newlines between each
    const formattedMessage = `Phone: ${formData.phone}\n\nEvent Type: ${formData.eventType}\n\nDate: ${formData.date}\n\nLocation: ${formData.location}\n\nAudience Size: ${formData.audience}\n\nAdditional Details: ${formData.message}`;

    const result = await submitForm(
      "Event Booking",
      formData.name,
      formData.email,
      formattedMessage
    );

    if (result.success) {
      setShowThankYou(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        eventType: "",
        date: "",
        location: "",
        audience: "",
        message: "",
      });
      resetStatus();
    } else {
      alert(
        "Sorry, there was an error sending your message. Please try again."
      );
    }
  };

  const handleBackToForm = () => {
    setShowThankYou(false);
  };

  return (
    <EventsContainer>
      <HeroSection>
        <HeroTitle>Live Poetry at Your Event</HeroTitle>
        <HeroSubtitle>
          Bring the magic of custom poetry to your special occasion with a
          vintage typewriter and an interactive poetry experience
        </HeroSubtitle>
      </HeroSection>

      <EventsSection>
        <SectionTitle>What Shana Brings to Your Event</SectionTitle>
        <EventsGrid>
          {eventTypes.map((eventType) => (
            <EventCard key={eventType.id}>
              <EventImage>
                <img src={eventType.image} alt={eventType.title} />
              </EventImage>
              <EventContent>
                <EventTitle>{eventType.title}</EventTitle>
                <EventDescription>{eventType.description}</EventDescription>
                <EventDetails>
                  <div className="detail">
                    <strong>Perfect for:</strong>
                  </div>
                  {eventType.examples.map((example, index) => (
                    <div key={index} className="detail">
                      • {example}
                    </div>
                  ))}
                </EventDetails>
              </EventContent>
            </EventCard>
          ))}
        </EventsGrid>
      </EventsSection>

      <TestimonialsSection>
        <SectionTitle>What Clients Say</SectionTitle>
        <TestimonialsGrid>
          <TestimonialCard>
            <TestimonialText>
              "Shana brought an incredible energy to our wedding. Her poetry
              station was the highlight of our reception - guests are still
              talking about it months later."
            </TestimonialText>
            <TestimonialAuthor>
              - Sarah & Michael, Wedding Couple
            </TestimonialAuthor>
          </TestimonialCard>
          <TestimonialCard>
            <TestimonialText>
              "The custom poems Shana created for our corporate holiday party
              were absolutely perfect. She captured our company culture
              beautifully."
            </TestimonialText>
            <TestimonialAuthor>
              - Jennifer Chen, Marketing Director
            </TestimonialAuthor>
          </TestimonialCard>
          <TestimonialCard>
            <TestimonialText>
              "Shana's performance at our art gallery opening was mesmerizing.
              She created such intimate moments with our guests."
            </TestimonialText>
            <TestimonialAuthor>
              - David Rodriguez, Gallery Owner
            </TestimonialAuthor>
          </TestimonialCard>
          <TestimonialCard>
            <TestimonialText>
              "Our graduation celebration was made unforgettable by Shana's
              poetry. Each graduate received a personalized poem that brought
              tears to their eyes."
            </TestimonialText>
            <TestimonialAuthor>
              - Dr. Emily Thompson, University Dean
            </TestimonialAuthor>
          </TestimonialCard>
          <TestimonialCard>
            <TestimonialText>
              "Shana transformed our baby shower into a magical experience. The
              poems she wrote for each guest were so touching and personal."
            </TestimonialText>
            <TestimonialAuthor>- Maria & James, New Parents</TestimonialAuthor>
          </TestimonialCard>
          <TestimonialCard>
            <TestimonialText>
              "The poetry station at our anniversary party was the perfect
              touch. Shana's ability to capture 25 years of love in a few lines
              was incredible."
            </TestimonialText>
            <TestimonialAuthor>
              - Robert & Linda, Anniversary Couple
            </TestimonialAuthor>
          </TestimonialCard>
        </TestimonialsGrid>
      </TestimonialsSection>

      <BookingSection>
        <BookingContainer>
          <BookingTitle>Book Your Live Poetry Experience</BookingTitle>
          <div
            style={{
              textAlign: "center",
              marginBottom: "3rem",
              maxWidth: "800px",
              margin: "0 auto 3rem auto",
            }}
          >
            <h3
              style={{
                fontSize: "1.5rem",
                marginBottom: "1.5rem",
                color: colors.inkBlack,
                fontFamily: fonts.heading,
              }}
            >
              The Experience
            </h3>
            <p
              style={{
                fontSize: "1.1rem",
                lineHeight: "1.8",
                color: colors.coolTaupe,
                marginBottom: "1.5rem",
              }}
            >
              Shana brings an air of timeless elegance to your event, arriving
              with her antique typewriter and creating an intimate, bespoke
              poetry experience. At her beautifully appointed station, guests
              are invited to share a subject—something meaningful to them or
              someone they wish to honor. With practiced artistry, Shana
              composes a personalized, one-of-a-kind poem in mere minutes. Each
              piece is typed onto exquisite handmade paper, which can be
              discreetly embossed with your company logo, event details, or a
              custom message.
            </p>
            <p
              style={{
                fontSize: "1.1rem",
                lineHeight: "1.8",
                color: colors.coolTaupe,
                marginBottom: "1.5rem",
              }}
            >
              Whether kept as a personal memento or gifted to someone special,
              every poem becomes a treasured keepsake of the occasion.
            </p>
          </div>
          {showThankYou ? (
            <ThankYouMessage>
              <ThankYouTitle>Thank You</ThankYouTitle>
              <ThankYouText>
                "Every event begins with a conversation, and every conversation
                begins with trust. Thank you for trusting me with your special
                occasion."
              </ThankYouText>
              <ThankYouText>
                Shana will review your details and reach out within 24 hours to
                discuss how we can make your event truly unforgettable. Your
                vision deserves the perfect poetry experience.
              </ThankYouText>
              <BackButton onClick={handleBackToForm}>
                Book Another Event
              </BackButton>
            </ThankYouMessage>
          ) : (
            <BookingForm onSubmit={handleSubmit} id="booking-form">
              <FormGroup>
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="eventType">Type of Event *</label>
                <select
                  id="eventType"
                  name="eventType"
                  value={formData.eventType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select an event type</option>
                  <option value="wedding">Wedding/Engagement Party</option>
                  <option value="corporate">Corporate Event</option>
                  <option value="birthday">Birthday Party</option>
                  <option value="graduation">Graduation Celebration</option>
                  <option value="baby-shower">Baby Shower</option>
                  <option value="anniversary">Anniversary Party</option>
                  <option value="holiday-party">Holiday Party</option>
                  <option value="cultural-event">
                    Cultural/Community Event
                  </option>
                  <option value="art-gallery">Art Gallery Opening</option>
                  <option value="literary-event">Literary Event</option>
                  <option value="fundraiser">Fundraiser</option>
                  <option value="other">Other</option>
                </select>
              </FormGroup>

              <FormGroup>
                <label htmlFor="date">Preferred Date</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="location">Event Location</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="City, State or Venue"
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="audience">Expected Audience Size</label>
                <select
                  id="audience"
                  name="audience"
                  value={formData.audience}
                  onChange={handleInputChange}
                >
                  <option value="">Select audience size</option>
                  <option value="1-50">1-50 people</option>
                  <option value="51-100">51-100 people</option>
                  <option value="101-250">101-250 people</option>
                  <option value="251-500">251-500 people</option>
                  <option value="500+">500+ people</option>
                </select>
              </FormGroup>

              <FormGroup>
                <label htmlFor="message">Event Details & Requirements</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell me about your event, any specific themes, if you'd like branded paper with your logo, expected number of guests, and any other details..."
                />
              </FormGroup>

              <SubmitButton type="submit" disabled={isSubmitting}>
                <FaEnvelope />{" "}
                {isSubmitting ? "Sending..." : "Book Your Poetry Experience"}
              </SubmitButton>
            </BookingForm>
          )}
        </BookingContainer>
      </BookingSection>
    </EventsContainer>
  );
};

export default Events;
